import { styled } from "goober";
import type { ReactNode } from "react";
import { useMemo, useRef, useState } from "react";
import {
  canonicalManagedConnectorId,
  isManagedAiConnectorId,
  MANAGED_AI_CONNECTOR_LABEL,
} from "../../../shared/aiProviders/managedAiConnector";
import { connectorUsageSourceLabel } from "../../../shared/connectorUsageSources";
import { queries, roundedHoursAgo } from "../../../shared/queries";
import { useManagedAIProvider } from "../../hooks/useManagedAIProvider";
import { useQuery } from "../../hooks/useQuery";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { connectorRawTypeLabel } from "../../lib/connector";
import { connectorProducesTokens } from "../../lib/connectorDirectory";
import {
  type BudgetStatus,
  buildUsagePriceLookup,
  computeBudgetStatuses,
  computeConnectorCosts,
  computeConnectorUsageOverview,
  computeConnectorUsageSummaries,
  computeRequestVolumeByDay,
  computeTokensByModel,
  computeUsageBySource,
  type ConnectorUsageOverview,
  type ConnectorUsageSummary,
  filterRowsBySpace,
  managedAiDefaultPrices,
  type ModelTokenTotals,
  NO_SPACE,
  type SourceUsageSummary,
} from "../../lib/connectorUsage";
import { formatBytes } from "../../lib/formatBytes";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { createdByName } from "../ConnectorRow";
import { ProviderLogo } from "../ProviderLogo";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { ListTable, type SortDirection } from "../ListTable";
import { Skeleton } from "../Skeleton";
import { Text } from "../Text";
import { StatCell } from "./AiOverviewStats";
import {
  formatCacheHitRate,
  formatEstimatedCostUsd,
  formatTokenCount,
} from "./aiUsage";
import { ConnectorRequestVolumePanel } from "./ConnectorRequestVolumePanel";
import { ControlButton } from "./ControlButton";
import { MonitoringLayout } from "./MonitoringLayout";
import { NameBody, NameCell, ScrollArea, TableCard } from "./monitoringList";
import {
  MONITORING_PANEL_PADDING,
  SPACE_COLORS,
} from "./monitoringPanelLayout";
import { ScopeDot } from "./ScopeDot";
import { Content, Panel, PanelHeader, TopRow } from "./workflowsLayout";

type SortColumn = "requests" | "errors" | "bytes" | "tokens" | "cost";
type ModelSortColumn = "input" | "output" | "cache" | "tokens" | "cost";

const DAY_MS = 86_400_000;
const WINDOW_OPTIONS = [
  { value: "7", label: "7 days", ms: 7 * DAY_MS },
  { value: "30", label: "30 days", ms: 30 * DAY_MS },
  { value: "90", label: "90 days", ms: 90 * DAY_MS },
] as const;

function windowMs(param: string): number {
  const option = WINDOW_OPTIONS.find((o) => o.value === param);
  return option?.ms ?? 30 * DAY_MS;
}

function windowLabel(param: string): string {
  const option = WINDOW_OPTIONS.find((o) => o.value === param);
  return option?.label ?? "30 days";
}

const ALL_CONNECTORS = "__all__";
const OTHER_CONNECTOR = "__other__";
const CONNECTOR_MONITORING_MEASURES = [
  "requests",
  "errors",
  "bytes_in",
  "bytes_out",
  "input_tokens",
  "output_tokens",
  "cache_read_tokens",
  "cache_write_tokens",
];

// The ledger meters managed AI per provider (`3b:managed-ai:<provider>`); rewrite
// those ids onto the base sentinel so every grouping below folds managed usage
// into one connector instead of a row per provider.
function canonicalizeManagedRows<T extends { connectorId: string }>(
  rows: readonly T[]
): readonly T[] {
  let changed = false;
  const out = rows.map((row) => {
    const connectorId = canonicalManagedConnectorId(row.connectorId);
    if (connectorId === row.connectorId) return row;
    changed = true;
    return { ...row, connectorId };
  });
  return changed ? out : rows;
}

const EmptyStateArea = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Strip = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  padding: ${MONITORING_PANEL_PADDING}px;
  border-radius: 12px;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  container-type: inline-size;
`;

const Stats = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(24px, 5cqi, 64px);
  align-items: flex-start;
`;

const Pickers = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ConnectorTrigger = styled(ControlButton)`
  max-width: 280px;
`;

const BudgetCellStack = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const BUDGET_LEVEL_COLORS = {
  ok: "var(--text-secondary)",
  warn: "var(--orange-600)",
  over: "var(--red-600)",
} as const;

function BudgetedCell({
  value,
  status,
  format,
}: {
  value: string;
  status: BudgetStatus | undefined;
  format: (amount: number) => string;
}) {
  if (!status) return <Text variant="text-small">{value}</Text>;
  return (
    <BudgetCellStack>
      <Text variant="text-small">{value}</Text>
      <Text variant="text-small" color={BUDGET_LEVEL_COLORS[status.level]}>
        of {format(status.limit)}
      </Text>
    </BudgetCellStack>
  );
}

function OverviewStrip({
  overview,
  estimatedCostUsd,
  showTokens,
  showSpend,
  pickers,
}: {
  overview: ConnectorUsageOverview;
  estimatedCostUsd: number | null;
  showTokens: boolean;
  showSpend: boolean;
  pickers: ReactNode;
}) {
  const stats = [
    { label: "Requests", value: overview.requests.toLocaleString("en-US") },
    { label: "Errors", value: overview.errors.toLocaleString("en-US") },
    { label: "Bytes", value: formatBytes(overview.bytes) },
    ...(showTokens
      ? [
          {
            label: "Tokens",
            value:
              overview.tokens == null ? "—" : formatTokenCount(overview.tokens),
          },
          {
            label: "Cache hit rate",
            value: formatCacheHitRate(
              overview.cacheReadTokens,
              overview.inputTokens + overview.cacheReadTokens
            ),
          },
        ]
      : []),
    ...(showSpend
      ? [
          {
            label: "Estimated spend",
            value: formatEstimatedCostUsd(estimatedCostUsd),
          },
        ]
      : []),
  ];
  return (
    <Strip>
      <Stats>
        {stats.map((stat) => (
          <StatCell key={stat.label}>
            <Text variant="text-small" color="var(--text-secondary)">
              {stat.label}
            </Text>
            <Text variant="display-md">{stat.value}</Text>
          </StatCell>
        ))}
      </Stats>
      {pickers}
    </Strip>
  );
}

type ModelRow = ModelTokenTotals & { estimatedCostUsd: number | null };

function ConnectorModelsPanel({
  models,
  windowLabelText,
}: {
  models: readonly ModelRow[];
  windowLabelText: string;
}) {
  const [sortColumn, setSortColumn] = useState<ModelSortColumn>("tokens");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const sorted = useMemo(() => {
    const dir = sortDirection === "asc" ? 1 : -1;
    const value = (row: ModelRow): number => {
      switch (sortColumn) {
        case "input":
          return row.inputTokens;
        case "output":
          return row.outputTokens;
        case "cache":
          return row.cacheReadTokens;
        case "tokens":
          return row.totalTokens;
        case "cost":
          return row.estimatedCostUsd ?? -1;
      }
    };
    return [...models].sort(
      (a, b) => dir * (value(a) - value(b)) || a.model.localeCompare(b.model)
    );
  }, [models, sortColumn, sortDirection]);

  const onSort = (column: string) => {
    const next = column as ModelSortColumn;
    if (next === sortColumn) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(next);
      setSortDirection("desc");
    }
  };

  return (
    <Panel style={{ minHeight: 0 }}>
      <PanelHeader>
        <Text variant="text-small-md">Models</Text>
        <Text variant="text-small" color="var(--text-secondary)">
          Input, output, and cache tokens per model in the last{" "}
          {windowLabelText}.
        </Text>
      </PanelHeader>
      <TableCard>
        <ScrollArea>
          <ListTable
            headers={{
              model: "Model",
              input: "Input",
              output: "Output",
              cache: "Cache",
              tokens: "Tokens",
              cost: "Est. cost",
            }}
            columns={{
              model: "flex",
              input: "md",
              output: "md",
              cache: "md",
              tokens: "md",
              cost: "md",
            }}
            sortableColumns={["input", "output", "cache", "tokens", "cost"]}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
            emptyState={
              <Text variant="text-regular" color="var(--text-secondary)">
                No token usage recorded in the last {windowLabelText}.
              </Text>
            }
            items={sorted.map((row) => ({
              key: row.model,
              model: (
                <Text variant="text-regular-md" ellipsis>
                  {row.model || "Unknown model"}
                </Text>
              ),
              input: (
                <Text variant="text-small">
                  {formatTokenCount(row.inputTokens)}
                </Text>
              ),
              output: (
                <Text variant="text-small">
                  {formatTokenCount(row.outputTokens)}
                </Text>
              ),
              cache: (
                <Text variant="text-small">
                  {formatCacheHitRate(
                    row.cacheReadTokens,
                    row.inputTokens + row.cacheReadTokens
                  )}
                </Text>
              ),
              tokens: (
                <Text variant="text-small">
                  {formatTokenCount(row.totalTokens)}
                </Text>
              ),
              cost: (
                <Text variant="text-small">
                  {formatEstimatedCostUsd(row.estimatedCostUsd)}
                </Text>
              ),
            }))}
          />
        </ScrollArea>
      </TableCard>
    </Panel>
  );
}

type SourceRow = SourceUsageSummary & { estimatedCostUsd: number | null };

function ConnectorSourcesPanel({
  sources,
  showTokens,
  showSpend,
  windowLabelText,
  standalone = false,
}: {
  sources: readonly SourceRow[];
  showTokens: boolean;
  showSpend: boolean;
  windowLabelText: string;
  standalone?: boolean;
}) {
  return (
    <Panel style={standalone ? { flex: 1, minHeight: 340 } : { minHeight: 0 }}>
      <PanelHeader>
        <Text variant="text-small-md">Sources</Text>
        <Text variant="text-small" color="var(--text-secondary)">
          Where connector requests came from in the last {windowLabelText}.
        </Text>
      </PanelHeader>
      <TableCard>
        <ScrollArea>
          <ListTable
            headers={{
              source: "Source",
              requests: "Requests",
              errors: "Errors",
              bytes: "Bytes",
              ...(showTokens ? { tokens: "Tokens" } : {}),
              ...(showSpend ? { cost: "Est. cost" } : {}),
            }}
            columns={{
              source: "flex",
              requests: "md",
              errors: "md",
              bytes: "md",
              ...(showTokens ? { tokens: "md" } : {}),
              ...(showSpend ? { cost: "md" } : {}),
            }}
            emptyState={
              <Text variant="text-regular" color="var(--text-secondary)">
                No connector usage recorded in the last {windowLabelText}.
              </Text>
            }
            items={sources.map((row) => ({
              key: row.source,
              source: (
                <Text variant="text-regular-md" ellipsis>
                  {connectorUsageSourceLabel(row.source)}
                </Text>
              ),
              requests: (
                <Text variant="text-small">
                  {row.requests.toLocaleString("en-US")}
                </Text>
              ),
              errors: (
                <Text variant="text-small">
                  {row.errors.toLocaleString("en-US")}
                </Text>
              ),
              bytes: <Text variant="text-small">{formatBytes(row.bytes)}</Text>,
              ...(showTokens
                ? {
                    tokens: (
                      <Text variant="text-small">
                        {row.tokens == null
                          ? "—"
                          : formatTokenCount(row.tokens)}
                      </Text>
                    ),
                  }
                : {}),
              ...(showSpend
                ? {
                    cost: (
                      <Text variant="text-small">
                        {formatEstimatedCostUsd(row.estimatedCostUsd)}
                      </Text>
                    ),
                  }
                : {}),
            }))}
          />
        </ScrollArea>
      </TableCard>
    </Panel>
  );
}

export function Connectors() {
  const { window: windowParam = "30" } = useCurrentUrl(
    routes.monitoring.connectors
  );
  const since = roundedHoursAgo(windowMs(windowParam) / 3_600_000);
  const [usageRows, usageResult] = useQuery(
    queries.connectorUsageDailyLite({
      since,
      measures: CONNECTOR_MONITORING_MEASURES,
    })
  );
  const [attributionWorkflows] = useQuery(queries.workflowSpaces());
  // Budgets are evaluated over their own rolling 30-day window, never the
  // display window; when the display window is shorter this subscribes to the
  // full 30 days (identical queries dedupe), otherwise it reuses the loaded
  // rows filtered down to 30 days.
  const since30 = roundedHoursAgo(30 * 24);
  const [budgetUsageRows, budgetUsageResult] = useQuery(
    queries.connectorUsageDailyLite({
      since: Math.min(since, since30),
      measures: CONNECTOR_MONITORING_MEASURES,
    })
  );
  const [budgets, budgetsResult] = useQuery(queries.monitoringUsageBudgets());
  const [connectors, connectorsResult] = useQuery(queries.connectedAppsLite());
  const [priceRows, pricesResult] = useQuery(queries.monitoringUsagePrices());

  // Hold the last completed windowed result so changing the time window swaps
  // data in place instead of blanking while the new window's query reloads.
  const lastUsage = useRef(usageRows);
  if (usageResult.type === "complete") lastUsage.current = usageRows;
  const rawUsageRowsView =
    usageResult.type === "complete" ? usageRows : lastUsage.current;
  const usageRowsView = useMemo(
    () => canonicalizeManagedRows(rawUsageRowsView ?? []),
    [rawUsageRowsView]
  );

  const lastBudgetUsage = useRef(budgetUsageRows);
  if (budgetUsageResult.type === "complete") {
    lastBudgetUsage.current = budgetUsageRows;
  }
  const rawBudgetUsageView =
    budgetUsageResult.type === "complete"
      ? budgetUsageRows
      : lastBudgetUsage.current;
  const budgetUsageView = useMemo(
    () => canonicalizeManagedRows(rawBudgetUsageView ?? []),
    [rawBudgetUsageView]
  );

  const [sortColumn, setSortColumn] = useState<SortColumn>("requests");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(
    null
  );
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const displaySpaceName = useSpaceDisplayName();
  const { managedProvider } = useManagedAIProvider();

  const managedAiLogo = (size: number) =>
    managedProvider ? (
      <ProviderLogo provider={managedProvider} size={size} />
    ) : (
      <ConnectorLogo
        template={{ name: MANAGED_AI_CONNECTOR_LABEL, identifier: "unknown" }}
        size={size}
      />
    );

  const connectorById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof connectors>[number]>();
    for (const connector of connectors ?? []) map.set(connector.id, connector);
    return map;
  }, [connectors]);

  const allSummaries = useMemo(
    () => computeConnectorUsageSummaries(usageRowsView ?? []),
    [usageRowsView]
  );

  const connectorOptions = useMemo(
    () =>
      [...allSummaries].sort((a, b) => {
        const an = connectorById.get(a.connectorId)?.name ?? "";
        const bn = connectorById.get(b.connectorId)?.name ?? "";
        return (
          an.localeCompare(bn) || a.connectorId.localeCompare(b.connectorId)
        );
      }),
    [allSummaries, connectorById]
  );

  const spaceByWorkflow = useMemo(() => {
    const map = new Map<string, DisplayableSpace>();
    for (const workflow of attributionWorkflows ?? []) {
      if (workflow.space) map.set(workflow.id, workflow.space);
    }
    return map;
  }, [attributionWorkflows]);

  const spaceOptions = useMemo(() => {
    const byId = new Map<string, DisplayableSpace>();
    let hasUnattributed = false;
    for (const row of usageRowsView ?? []) {
      if (row.workflowId === null) {
        hasUnattributed = true;
        continue;
      }
      const space = spaceByWorkflow.get(row.workflowId);
      if (space && space.mode !== "personal") byId.set(space.id, space);
    }
    const spaces = [...byId.values()]
      .map((space) => ({ id: space.id, label: displaySpaceName(space) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { spaces, hasUnattributed };
  }, [usageRowsView, spaceByWorkflow, displaySpaceName]);

  const spaceSelectionValid =
    selectedSpaceId != null &&
    (selectedSpaceId === NO_SPACE
      ? spaceOptions.hasUnattributed
      : spaceOptions.spaces.some((s) => s.id === selectedSpaceId));
  const effectiveSpaceId = spaceSelectionValid ? selectedSpaceId : null;

  const spaceUsageRows = useMemo(
    () =>
      filterRowsBySpace(usageRowsView ?? [], effectiveSpaceId, spaceByWorkflow),
    [usageRowsView, effectiveSpaceId, spaceByWorkflow]
  );
  const summaries = useMemo(
    () =>
      effectiveSpaceId
        ? computeConnectorUsageSummaries(spaceUsageRows)
        : allSummaries,
    [effectiveSpaceId, spaceUsageRows, allSummaries]
  );

  const selectionValid =
    selectedConnectorId != null &&
    allSummaries.some((s) => s.connectorId === selectedConnectorId);
  const effectiveConnectorId = selectionValid ? selectedConnectorId : null;

  const filteredRows = useMemo(
    () =>
      effectiveConnectorId
        ? spaceUsageRows.filter((r) => r.connectorId === effectiveConnectorId)
        : spaceUsageRows,
    [spaceUsageRows, effectiveConnectorId]
  );
  const rows = useMemo(
    () =>
      effectiveConnectorId
        ? summaries.filter((s) => s.connectorId === effectiveConnectorId)
        : summaries,
    [summaries, effectiveConnectorId]
  );
  const overview = useMemo(
    () => computeConnectorUsageOverview(rows, filteredRows),
    [rows, filteredRows]
  );
  const volume = useMemo(
    () => computeRequestVolumeByDay(filteredRows, since, Date.now()),
    [filteredRows, since]
  );

  const volumeSeries = useMemo(() => {
    const coloredCount =
      rows.length > SPACE_COLORS.length ? SPACE_COLORS.length - 1 : rows.length;
    const series: { key: string; label: string; color: string }[] = [];
    const seriesKeyOf = new Map<string, string>();
    rows.forEach((row, i) => {
      if (i < coloredCount) {
        seriesKeyOf.set(row.connectorId, row.connectorId);
        series.push({
          key: row.connectorId,
          label: isManagedAiConnectorId(row.connectorId)
            ? MANAGED_AI_CONNECTOR_LABEL
            : (connectorById.get(row.connectorId)?.name ?? "Unknown connector"),
          color: SPACE_COLORS[i % SPACE_COLORS.length]!,
        });
      } else {
        seriesKeyOf.set(row.connectorId, OTHER_CONNECTOR);
      }
    });
    if (rows.length > coloredCount) {
      series.push({
        key: OTHER_CONNECTOR,
        label: "Other",
        color: "var(--gray-600)",
      });
    }
    return { series, seriesKeyOf };
  }, [rows, connectorById]);

  const anyAiConnector = useMemo(
    () =>
      rows.some(
        (row) =>
          isManagedAiConnectorId(row.connectorId) ||
          connectorProducesTokens(
            connectorById.get(row.connectorId)?.identifier
          )
      ),
    [rows, connectorById]
  );

  const prices = useMemo(
    () => buildUsagePriceLookup(priceRows ?? [], managedAiDefaultPrices()),
    [priceRows]
  );

  // Spend is meaningful for any connector with a configured price (tokens for AI
  // ones, requests/data for the rest), so it surfaces more widely than tokens.
  const pricedConnectors = useMemo(() => {
    const set = new Set<string>();
    for (const row of priceRows ?? []) {
      if (row.connectorId && row.spaceId == null) {
        set.add(row.connectorId);
      }
    }
    return set;
  }, [priceRows]);
  const anySpend = useMemo(
    () =>
      anyAiConnector ||
      rows.some((row) => pricedConnectors.has(row.connectorId)),
    [anyAiConnector, rows, pricedConnectors]
  );
  const costs = useMemo(
    () => computeConnectorCosts(filteredRows, prices),
    [filteredRows, prices]
  );
  const costByModel = costs.byModel;
  const costByConnector = costs.byConnector;
  const totalCost = costs.total;

  // Budgets check the connector-wide total when no space is selected, otherwise
  // that space's slice against its per-space limits.
  const budgetWindowRows = useMemo(
    () =>
      filterRowsBySpace(
        (budgetUsageView ?? []).filter((row) => row.day >= since30),
        effectiveSpaceId,
        spaceByWorkflow
      ),
    [budgetUsageView, since30, effectiveSpaceId, spaceByWorkflow]
  );
  const budgetStatuses = useMemo(
    () =>
      computeBudgetStatuses(
        computeConnectorCosts(budgetWindowRows, prices).byConnector,
        budgets ?? [],
        effectiveSpaceId
      ),
    [budgetWindowRows, prices, budgets, effectiveSpaceId]
  );

  const tokensByModel = useMemo(() => {
    return computeTokensByModel(filteredRows).map((model) => ({
      ...model,
      estimatedCostUsd: costByModel.get(model.model) ?? null,
    }));
  }, [filteredRows, costByModel]);

  const costBySource = costs.bySource;
  const usageBySource = useMemo(() => {
    return computeUsageBySource(filteredRows).map((row) => ({
      ...row,
      estimatedCostUsd: costBySource.get(row.source) ?? null,
    }));
  }, [filteredRows, costBySource]);

  const sorted = useMemo(() => {
    const dir = sortDirection === "asc" ? 1 : -1;
    const value = (row: ConnectorUsageSummary): number => {
      switch (sortColumn) {
        case "requests":
          return row.requests;
        case "errors":
          return row.errors;
        case "bytes":
          return row.bytes;
        case "tokens":
          return row.tokens ?? -1;
        case "cost":
          return costByConnector.get(row.connectorId) ?? -1;
      }
    };
    return [...rows].sort(
      (a, b) =>
        dir * (value(a) - value(b)) ||
        a.connectorId.localeCompare(b.connectorId)
    );
  }, [rows, sortColumn, sortDirection, costByConnector]);

  const onSort = (column: string) => {
    const next = column as SortColumn;
    if (next === sortColumn) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(next);
      setSortDirection("desc");
    }
  };

  const isLoading =
    usageResult.type !== "complete" ||
    connectorsResult.type !== "complete" ||
    pricesResult.type !== "complete" ||
    budgetUsageResult.type !== "complete" ||
    budgetsResult.type !== "complete";

  // Only block the whole page on the first load; window changes refetch the
  // windowed queries but should keep the rendered content (with held data).
  const hasLoadedOnce = useRef(false);
  if (!isLoading) hasLoadedOnce.current = true;

  if (isLoading && !hasLoadedOnce.current) {
    return (
      <MonitoringLayout>
        <Content aria-hidden>
          <Skeleton style={{ width: "100%", flex: 1, borderRadius: 12 }} />
        </Content>
      </MonitoringLayout>
    );
  }

  if (allSummaries.length === 0) {
    return (
      <MonitoringLayout>
        <Content>
          <EmptyStateArea>
            <EmptyState
              iconName="api-key-custom"
              title="No connector usage yet"
              description="Requests made through a connector appear here within a minute."
              maxWidth={420}
            />
          </EmptyStateArea>
        </Content>
      </MonitoringLayout>
    );
  }

  const label = windowLabel(windowParam);
  const hasModels = tokensByModel.length > 0;
  const selectedConnector = effectiveConnectorId
    ? connectorById.get(effectiveConnectorId)
    : undefined;
  const selectedSpaceIndex =
    effectiveSpaceId && effectiveSpaceId !== NO_SPACE
      ? spaceOptions.spaces.findIndex((s) => s.id === effectiveSpaceId)
      : -1;
  const selectedSpace =
    selectedSpaceIndex >= 0 ? spaceOptions.spaces[selectedSpaceIndex] : null;

  const connectorPicker = (
    <Dropdown
      align="bottom"
      opener={
        <ConnectorTrigger type="button">
          {effectiveConnectorId == null ? (
            <Icon name="layers" size={16} color="var(--text-primary)" />
          ) : selectedConnector ? (
            <ConnectorLogo app={selectedConnector} size={16} />
          ) : isManagedAiConnectorId(effectiveConnectorId) ? (
            managedAiLogo(16)
          ) : (
            <ConnectorLogo
              template={{ name: "Unknown connector", identifier: "unknown" }}
              size={16}
            />
          )}
          <Text variant="text-small-md" ellipsis>
            {effectiveConnectorId
              ? (selectedConnector?.name ??
                (isManagedAiConnectorId(effectiveConnectorId)
                  ? MANAGED_AI_CONNECTOR_LABEL
                  : "Unknown connector"))
              : "All connectors"}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </ConnectorTrigger>
      }
      sections={[
        {
          items: [
            ...(allSummaries.length > 1
              ? [
                  {
                    type: "button" as const,
                    key: ALL_CONNECTORS,
                    left: (
                      <Icon
                        name="layers"
                        size={16}
                        color="var(--text-primary)"
                      />
                    ),
                    label: "All connectors",
                    selected: effectiveConnectorId === null,
                    onClick: () => setSelectedConnectorId(null),
                  },
                ]
              : []),
            ...connectorOptions.map((summary) => {
              const connector = connectorById.get(summary.connectorId);
              const managed = isManagedAiConnectorId(summary.connectorId);
              const creator = connector
                ? createdByName(connector.createdBy)
                : null;
              return {
                type: "button" as const,
                key: summary.connectorId,
                left: connector ? (
                  <ConnectorLogo app={connector} size={16} />
                ) : managed ? (
                  managedAiLogo(16)
                ) : (
                  <ConnectorLogo
                    template={{
                      name: "Unknown connector",
                      identifier: "unknown",
                    }}
                    size={16}
                  />
                ),
                label:
                  connector?.name ??
                  (managed ? MANAGED_AI_CONNECTOR_LABEL : "Unknown connector"),
                subtitle: creator ? (
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {creator}
                  </Text>
                ) : undefined,
                selected: effectiveConnectorId === summary.connectorId,
                onClick: () => setSelectedConnectorId(summary.connectorId),
              };
            }),
          ],
        },
      ]}
    />
  );

  const spacePicker = (
    <Dropdown
      align="bottom"
      opener={
        <ControlButton type="button">
          {effectiveSpaceId === NO_SPACE ? (
            <ScopeDot style={{ background: "var(--gray-600)" }} />
          ) : selectedSpace ? (
            <ScopeDot
              style={{
                background:
                  SPACE_COLORS[selectedSpaceIndex % SPACE_COLORS.length],
              }}
            />
          ) : (
            <Icon name="layers" size={12} color="var(--text-primary)" />
          )}
          <Text variant="text-small-md" ellipsis>
            {effectiveSpaceId === NO_SPACE
              ? "No space"
              : (selectedSpace?.label ?? "All spaces")}
          </Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </ControlButton>
      }
      sections={[
        {
          items: [
            {
              type: "button" as const,
              key: "__all__",
              left: (
                <Icon name="layers" size={16} color="var(--text-primary)" />
              ),
              label: "All spaces",
              selected: effectiveSpaceId === null,
              onClick: () => setSelectedSpaceId(null),
            },
            ...spaceOptions.spaces.map((space, i) => ({
              type: "button" as const,
              key: space.id,
              left: (
                <ScopeDot
                  style={{ background: SPACE_COLORS[i % SPACE_COLORS.length] }}
                />
              ),
              label: space.label,
              selected: effectiveSpaceId === space.id,
              onClick: () => setSelectedSpaceId(space.id),
            })),
            ...(spaceOptions.hasUnattributed
              ? [
                  {
                    type: "button" as const,
                    key: NO_SPACE,
                    left: (
                      <ScopeDot style={{ background: "var(--gray-600)" }} />
                    ),
                    label: "No space",
                    selected: effectiveSpaceId === NO_SPACE,
                    onClick: () => setSelectedSpaceId(NO_SPACE),
                  },
                ]
              : []),
          ],
        },
      ]}
    />
  );

  const windowPicker = (
    <Dropdown
      align="bottom"
      opener={
        <ControlButton type="button">
          <Text variant="text-small-md">{label}</Text>
          <Icon name="chevron-down" size={12} color="var(--text-secondary)" />
        </ControlButton>
      }
      sections={[
        {
          items: WINDOW_OPTIONS.map((opt) => ({
            type: "button" as const,
            key: opt.value,
            left: null,
            label: opt.label,
            selected: windowParam === opt.value,
            onClick: () =>
              navigate(
                routes.monitoring.connectors,
                { window: opt.value },
                { replace: true }
              ),
          })),
        },
      ]}
    />
  );

  const connectorsPanel = (
    <Panel style={{ flex: 1, minHeight: 340 }}>
      <PanelHeader>
        <Text variant="text-small-md">Connectors</Text>
        <Text variant="text-small" color="var(--text-secondary)">
          Requests, errors, data transferred
          {anyAiConnector ? ", and tokens" : ""} per connector in the last{" "}
          {label}.
        </Text>
      </PanelHeader>
      <TableCard>
        <ScrollArea>
          <ListTable
            headers={{
              connector: "Connector",
              requests: "Requests",
              errors: "Errors",
              bytes: "Bytes",
              ...(anyAiConnector ? { tokens: "Tokens" } : {}),
              ...(anySpend ? { cost: "Est. cost" } : {}),
            }}
            columns={{
              connector: "flex",
              requests: "md",
              errors: "md",
              bytes: "md",
              ...(anyAiConnector ? { tokens: "md" } : {}),
              ...(anySpend ? { cost: "md" } : {}),
            }}
            sortableColumns={[
              "requests",
              "errors",
              "bytes",
              ...(anyAiConnector ? ["tokens"] : []),
              ...(anySpend ? ["cost"] : []),
            ]}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
            emptyState={
              <Text variant="text-regular" color="var(--text-secondary)">
                No connector usage recorded in the last {label}.
              </Text>
            }
            items={sorted.map((row) => {
              const connector = connectorById.get(row.connectorId);
              const managed = isManagedAiConnectorId(row.connectorId);
              const spendStatus = budgetStatuses.get(row.connectorId);
              return {
                key: row.connectorId,
                link: connector
                  ? makeUrl(routes.modal.connector, {
                      id: connector.id,
                      tab: "usage",
                    })
                  : undefined,
                connector: (
                  <NameCell>
                    <ConnectorLogoContainer
                      size={32}
                      borderRadius={8}
                      outline={false}
                      hoverBackground={false}
                    >
                      {connector ? (
                        <ConnectorLogo app={connector} size={16} />
                      ) : managed ? (
                        managedAiLogo(16)
                      ) : (
                        <ConnectorLogo
                          template={{
                            name: "Unknown connector",
                            identifier: "unknown",
                          }}
                          size={16}
                        />
                      )}
                    </ConnectorLogoContainer>
                    <NameBody>
                      <Text variant="text-regular-md" ellipsis>
                        {connector?.name ??
                          (managed
                            ? MANAGED_AI_CONNECTOR_LABEL
                            : "Unknown connector")}
                      </Text>
                      <Text
                        variant="text-small"
                        color="var(--text-secondary)"
                        ellipsis
                      >
                        {connector
                          ? connectorRawTypeLabel(connector)
                          : managed
                            ? "AI provider"
                            : row.connectorId}
                      </Text>
                    </NameBody>
                  </NameCell>
                ),
                requests: (
                  <Text variant="text-small">
                    {row.requests.toLocaleString("en-US")}
                  </Text>
                ),
                errors: (
                  <Text variant="text-small">
                    {row.errors.toLocaleString("en-US")}
                  </Text>
                ),
                bytes: (
                  <Text variant="text-small">{formatBytes(row.bytes)}</Text>
                ),
                ...(anyAiConnector
                  ? {
                      tokens: (
                        <Text variant="text-small">
                          {row.tokens == null
                            ? "—"
                            : formatTokenCount(row.tokens)}
                        </Text>
                      ),
                    }
                  : {}),
                ...(anySpend
                  ? {
                      cost: (
                        <BudgetedCell
                          value={formatEstimatedCostUsd(
                            costByConnector.get(row.connectorId) ?? null
                          )}
                          status={spendStatus}
                          format={formatEstimatedCostUsd}
                        />
                      ),
                    }
                  : {}),
              };
            })}
          />
        </ScrollArea>
      </TableCard>
    </Panel>
  );

  return (
    <MonitoringLayout>
      <Content>
        <OverviewStrip
          overview={overview}
          estimatedCostUsd={totalCost}
          showTokens={anyAiConnector}
          showSpend={anySpend}
          pickers={
            <Pickers>
              {connectorPicker}
              {spacePicker}
              {windowPicker}
            </Pickers>
          }
        />
        <ConnectorRequestVolumePanel
          volume={volume}
          series={volumeSeries.series}
          seriesKeyOf={volumeSeries.seriesKeyOf}
          windowLabel={label}
        />
        {connectorsPanel}
        {hasModels ? (
          <TopRow style={{ minHeight: 340 }}>
            <ConnectorModelsPanel
              models={tokensByModel}
              windowLabelText={label}
            />
            <ConnectorSourcesPanel
              sources={usageBySource}
              showTokens={anyAiConnector}
              showSpend={anySpend}
              windowLabelText={label}
            />
          </TopRow>
        ) : (
          <ConnectorSourcesPanel
            sources={usageBySource}
            showTokens={anyAiConnector}
            showSpend={anySpend}
            windowLabelText={label}
            standalone
          />
        )}
      </Content>
    </MonitoringLayout>
  );
}
